import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Users from './pages/Users'
import Organizations from './pages/Organizations'
import Projects from './pages/Projects'
import Members from './pages/Members'
import Invitations from './pages/Invitations'
import Billing from './pages/Billing'
import Notifications from './pages/Notifications'
import SystemHealth from './pages/SystemHealth'
import Settings from './pages/Settings'
import DiscountCodes from './pages/DiscountCodes'
import Education from './pages/Education'
import Login from './pages/Login'
import SetPassword from './pages/SetPassword'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import SchoolLayout from './components/school/SchoolLayout'
import SchoolOverview from './pages/school/SchoolOverview'
import SchoolTeachers from './pages/school/SchoolTeachers'
import SchoolTeacherDetail from './pages/school/SchoolTeacherDetail'
import SchoolStudents from './pages/school/SchoolStudents'
import SchoolStudentDetail from './pages/school/SchoolStudentDetail'
import SchoolClasses from './pages/school/SchoolClasses'
import SchoolClassDetail from './pages/school/SchoolClassDetail'
import SchoolUnitDetail from './pages/school/SchoolUnitDetail'
import SchoolActivityDetail from './pages/school/SchoolActivityDetail'
import SchoolReports from './pages/school/SchoolReports'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from './store'
import { setCredentials, logout } from './store/slices/authSlice'
import { useVerifyTokenQuery } from './store/api/authApi'
import { applyAccountLanguage } from './i18n'

function isOrgOwnerAdmin(role?: string | null) {
  return role === 'ADMIN'
}

function App() {
  const { isAuthenticated, token, user } = useSelector((state: RootState) => state.auth)
  const dispatch = useDispatch()

  // Key verify by token so a prior SUPER_ADMIN verify result cannot overwrite a new school-admin login.
  const { data: verifyData, error: verifyError, isLoading: isVerifying, isFetching } = useVerifyTokenQuery(token as string, {
    skip: !token,
  })

  useEffect(() => {
    if (!token) return

    if (verifyData?.success && verifyData.data?.user) {
      const verified = verifyData.data.user
      // Ignore stale cached verify payloads that don't match the current token session.
      if (user?.id && verified.id !== user.id) {
        return
      }
      dispatch(setCredentials({
        user: verified,
        token,
      }))
      if (verified.role === 'ADMIN') {
        applyAccountLanguage(verified.language)
      }
      return
    }

    if (verifyError && !isFetching) {
      dispatch(logout())
    }
  }, [verifyData, verifyError, token, dispatch, user?.id, isFetching])

  // Only block first paint when we have a token but no hydrated user yet.
  if (token && !user && (isVerifying || isFetching)) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const orgOwner = isAuthenticated && isOrgOwnerAdmin(user?.role)

  return (
    <div className="App">
      <Router>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated
                ? <Navigate to={orgOwner ? '/pending' : '/dashboard'} replace />
                : <Login />
            }
          />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/pending"
            element={
              !isAuthenticated
                ? <Navigate to="/login" replace />
                : orgOwner
                  ? <SchoolLayout />
                  : <Navigate to="/dashboard" replace />
            }
          >
            <Route index element={<SchoolOverview />} />
            <Route path="teachers" element={<SchoolTeachers />} />
            <Route path="teachers/:userId" element={<SchoolTeacherDetail />} />
            <Route path="students" element={<SchoolStudents />} />
            <Route path="students/:userId" element={<SchoolStudentDetail />} />
            <Route path="classes" element={<SchoolClasses />} />
            <Route path="classes/:projectId" element={<SchoolClassDetail />} />
            <Route path="units/:requestId" element={<SchoolUnitDetail />} />
            <Route path="activities/:ticketId" element={<SchoolActivityDetail />} />
            <Route path="reports" element={<SchoolReports />} />
          </Route>

          <Route
            path="/*"
            element={
              !isAuthenticated ? (
                <Navigate to="/login" replace />
              ) : orgOwner ? (
                <Navigate to="/pending" replace />
              ) : (
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/organizations" element={<Organizations />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/members" element={<Members />} />
                    <Route path="/invitations" element={<Invitations />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/discount-codes" element={<DiscountCodes />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/health" element={<SystemHealth />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/education" element={<Education />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Layout>
              )
            }
          />
        </Routes>
      </Router>
      
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            style: {
              background: '#10b981',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#ef4444',
            },
          },
        }}
      />
    </div>
  )
}

export default App
